import type { LessonModel } from '../Models/LessonModel';
import type { StudentSlotModel } from '../Models/StudentModel';

/**
 * The sync rules, with no network in them.
 *
 * This file builds the event bodies Magbit pushes to Google Calendar and works
 * out the changes it pulls back. Keeping it free of side effects is what makes
 * the conflict rule testable, and the conflict rule is the whole design:
 *
 *   **The calendar wins.** A value pulled from an event always overwrites the
 *   local lesson or slot. A tutor who moved a lesson in Google Calendar moved
 *   the lesson, and Magbit disagreeing with the calendar on their phone is the
 *   failure mode worth designing against.
 *
 * The second rule lives in the caller: an automatic sync never deletes
 * anything. Deleting happens only in a manual sync, and only after the tutor
 * has confirmed it.
 */

/** A Magbit event is recognised by its private properties, not by its title. */
export const MAGBIT_MARKER = 'magbit';

const SUMMARY_PATTERN = /^שיעור\s*·\s*(.+?)\s*$/;
const AMOUNT_PATTERN = /·\s*(\d+)\s*₪\s*$/;
const TIME_PATTERN = /^\d{2}:\d{2}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const pad = (value: number): string => String(value).padStart(2, '0');

export function isValidTime(value: unknown): value is string {
  if (typeof value !== 'string' || !TIME_PATTERN.test(value)) return false;
  const [hours, minutes] = value.split(':').map(Number);
  return (hours ?? -1) >= 0 && (hours ?? 99) <= 23 && (minutes ?? -1) >= 0 && (minutes ?? 99) <= 59;
}

function addDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const at = new Date(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days);
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

export interface EventEndpoint {
  date?: string;
  dateTime?: string;
  timeZone?: string;
}

export interface CalendarEvent {
  id?: string;
  summary?: string;
  description?: string;
  start?: EventEndpoint;
  end?: EventEndpoint;
  /** RRULE lines. A recurring slot is one event with a weekly rule, not 52 events. */
  recurrence?: string[];
  /** Google reports a deleted event as `cancelled` rather than 404 on a direct read. */
  status?: string;
  extendedProperties?: { private?: Record<string, string> };
}

/**
 * The hour written into an event for a lesson that has none of its own, so a
 * later pull can tell that hour apart from one the tutor chose.
 */
const DEFAULT_TIME_PROP = 'magbit_default_time';

/**
 * The wall-clock time from an RFC3339 stamp, read literally rather than
 * parsed. `2026-07-01T15:00:00+03:00` is 15:00 because that is what the tutor
 * sees in their calendar, whatever timezone this code happens to run in.
 */
export function timeFromEndpoint(endpoint: EventEndpoint | undefined): string | null {
  if (!endpoint?.dateTime) return null;
  const match = /T(\d{2}):(\d{2})/.exec(endpoint.dateTime);
  return match ? `${match[1]}:${match[2]}` : null;
}

export function dateFromEndpoint(endpoint: EventEndpoint | undefined): string | null {
  if (!endpoint) return null;
  if (endpoint.date && DATE_PATTERN.test(endpoint.date)) return endpoint.date;
  if (endpoint.dateTime && DATE_PATTERN.test(endpoint.dateTime.slice(0, 10))) {
    return endpoint.dateTime.slice(0, 10);
  }
  return null;
}

function computeEnd(date: string, time: string, durationMinutes: number): { date: string; time: string } {
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  const end = new Date(
    year ?? 1970, (month ?? 1) - 1, day ?? 1,
    hours ?? 0, (minutes ?? 0) + (durationMinutes || 60), 0, 0
  );
  return {
    date: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`,
    time: `${pad(end.getHours())}:${pad(end.getMinutes())}`
  };
}

export interface EventOptions {
  userId?: string;
  timeZone?: string;
  durationMinutes?: number;
  /**
   * Undefined means "fall back to 16:00". Pass null to force an all-day
   * event, which is what a lesson with no time and no recurring slot is.
   */
  defaultTime?: string | null;
}

export function buildLessonEvent(lesson: LessonModel, options: EventOptions = {}): CalendarEvent {
  const timeZone = options.timeZone ?? 'Asia/Jerusalem';
  const duration = options.durationMinutes ?? 60;
  const amount = Math.round(Number(lesson.amount) || 0);

  const fallback = options.defaultTime === undefined ? '16:00' : options.defaultTime;
  const ownTime = isValidTime(lesson.time) ? lesson.time : null;
  const time = ownTime ?? (isValidTime(fallback) ? fallback : null);

  const event: CalendarEvent = {
    summary: `שיעור · ${lesson.student}`,
    description: `Magbit · ${lesson.student} · ${amount} ₪`,
    extendedProperties: {
      private: {
        magbit: '1',
        magbit_kind: 'payment',
        magbit_user_id: options.userId ?? '',
        magbit_payment_id: String(lesson.id),
        // Recorded only when the hour was ours to invent. lessonChangesFromEvent
        // uses it to avoid reading that hour back as a decision the tutor made.
        ...(ownTime === null && time ? { [DEFAULT_TIME_PROP]: time } : {})
      }
    }
  };

  if (time) {
    const end = computeEnd(lesson.date, time, duration);
    event.start = { dateTime: `${lesson.date}T${time}:00`, timeZone };
    event.end = { dateTime: `${end.date}T${end.time}:00`, timeZone };
  } else {
    event.start = { date: lesson.date };
    event.end = { date: addDays(lesson.date, 1) };
  }
  return event;
}

export interface LessonChanges {
  date?: string;
  /** Null clears the time, which is what switching to an all-day event means. */
  time?: string | null;
  student?: string;
  amount?: string;
}

/**
 * What to change on a local lesson, given its calendar event. The calendar
 * wins on every field it can speak for.
 */
export function lessonChangesFromEvent(
  event: CalendarEvent,
  lesson: LessonModel,
  studentNames?: ReadonlySet<string>
): LessonChanges {
  const changes: LessonChanges = {};

  const date = dateFromEndpoint(event.start);
  if (date && date !== lesson.date) changes.date = date;

  const eventTime = timeFromEndpoint(event.start);
  const localTime = isValidTime(lesson.time) ? lesson.time : null;
  /*
   * A lesson with no time of its own is still pushed at an hour, so it shows
   * in the calendar as a lesson rather than an all-day banner. That hour was
   * Magbit's invention, so reading it straight back would answer a question
   * the tutor never asked and quietly fill in a time they left blank. It only
   * becomes a real time once it differs from the one that was written.
   */
  const ourTime = event.extendedProperties?.private?.[DEFAULT_TIME_PROP];
  const stillOurs = localTime === null && Boolean(ourTime) && eventTime === ourTime;
  if (!stillOurs && eventTime !== localTime) changes.time = eventTime;

  // A renamed event only moves the lesson to a student who exists. Creating
  // one from a title typed in a calendar would be too easy to do by accident.
  const student = SUMMARY_PATTERN.exec(event.summary ?? '')?.[1]?.trim();
  if (student && studentNames?.has(student) && student !== lesson.student) {
    changes.student = student;
  }

  const amount = AMOUNT_PATTERN.exec(event.description ?? '')?.[1];
  if (amount && String(Number(amount)) !== String(lesson.amount)) {
    changes.amount = amount;
  }

  return changes;
}

/**
 * A lesson read out of an event, for restoring one that is in the calendar
 * and missing locally. Null when the event does not describe a usable lesson.
 */
export function lessonFromEvent(event: CalendarEvent | null): {
  student: string; date: string; time: string | null; amount: string | null;
} | null {
  if (!event) return null;
  const date = dateFromEndpoint(event.start);
  if (!date) return null;
  const student = SUMMARY_PATTERN.exec(event.summary ?? '')?.[1]?.trim();
  if (!student) return null;
  return {
    student,
    date,
    time: timeFromEndpoint(event.start),
    amount: AMOUNT_PATTERN.exec(event.description ?? '')?.[1] ?? null
  };
}

export interface SlotEventInput {
  slotId: number;
  student: string;
  weekday: number;
  time: string;
  durationMinutes: number;
}

/** The first occurrence of `weekday` on or after `from`, as yyyy-mm-dd. */
export function nextWeekdayOnOrAfter(weekday: number, from: string): string {
  const [year, month, day] = from.split('-').map(Number);
  const at = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  at.setDate(at.getDate() + ((weekday - at.getDay() + 7) % 7));
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/**
 * A recurring slot as one weekly event, anchored to its next occurrence.
 *
 * One event with an RRULE rather than an event per week: the tutor edits the
 * series in one place, and a slot that moves to another hour is one change
 * here rather than an unbounded number of them.
 *
 * The summary deliberately does not match SUMMARY_PATTERN. A slot is not a
 * lesson, and a pull must never mistake one for the other.
 */
export function buildSlotEvent(slot: SlotEventInput, options: EventOptions & { from?: string } = {}): CalendarEvent {
  const timeZone = options.timeZone ?? 'Asia/Jerusalem';
  const time = isValidTime(slot.time) ? slot.time : '16:00';
  const today = options.from ?? (() => {
    const now = new Date();
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  })();
  const date = nextWeekdayOnOrAfter(slot.weekday, today);
  const end = computeEnd(date, time, slot.durationMinutes || 60);

  return {
    summary: `שיעור קבוע · ${slot.student}`,
    description: `Magbit · ${slot.student}`,
    start: { dateTime: `${date}T${time}:00`, timeZone },
    end: { dateTime: `${end.date}T${end.time}:00`, timeZone },
    recurrence: ['RRULE:FREQ=WEEKLY'],
    extendedProperties: {
      private: {
        magbit: '1',
        magbit_kind: 'slot',
        magbit_user_id: options.userId ?? '',
        magbit_slot_id: String(slot.slotId)
      }
    }
  };
}

export interface SlotChanges {
  weekday?: number;
  time?: string;
  durationMinutes?: number;
}

/** What to change on a recurring slot, given the event standing for it. */
export function slotChangesFromEvent(event: CalendarEvent, slot: StudentSlotModel): SlotChanges {
  const date = dateFromEndpoint(event.start);
  if (!date) return {};
  const [year, month, day] = date.split('-').map(Number);
  const weekday = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1).getDay();
  const time = timeFromEndpoint(event.start);

  const changes: SlotChanges = {};
  if (weekday !== Number(slot.weekday)) changes.weekday = weekday;
  if (time && time !== slot.time) changes.time = time;

  const endDate = dateFromEndpoint(event.end);
  const endTime = timeFromEndpoint(event.end);
  if (time && endTime && endDate) {
    const start = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1,
      Number(time.slice(0, 2)), Number(time.slice(3, 5)));
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    const end = new Date(endYear ?? 1970, (endMonth ?? 1) - 1, endDay ?? 1,
      Number(endTime.slice(0, 2)), Number(endTime.slice(3, 5)));
    const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60_000);
    // A duration outside a sane lesson length means the event was edited into
    // something that is not a lesson any more, so it is left alone.
    if (durationMinutes >= 5 && durationMinutes <= 1440
      && durationMinutes !== Number(slot.durationMinutes)) {
      changes.durationMinutes = durationMinutes;
    }
  }
  return changes;
}
